import { supabase } from "@/platform/lib/supabase";
import { buildStudyPlan, currentWeekStart } from "./algorithm";
import type { DisciplineSnapshot, StudyPlan } from "./types";

/**
 * Carrega plano da semana corrente do usuário, se houver.
 */
export async function fetchCurrentWeekPlan(userId: string): Promise<StudyPlan | null> {
  const weekStart = currentWeekStart();
  const { data, error } = await supabase
    .from("einstein_study_plans")
    .select("plan")
    .eq("user_id", userId)
    .eq("week_start", weekStart)
    .maybeSingle();
  if (error) {
    console.error("[study-plan] fetch", error);
    return null;
  }
  return (data?.plan as StudyPlan | undefined) ?? null;
}

/**
 * Carrega o último diagnóstico (sessão concluída do tipo 'diagnosis') do usuário
 * e calcula snapshots por disciplina.
 */
export async function fetchLatestDiagnosisSnapshots(userId: string): Promise<{
  sessionId: string | null;
  snapshots: DisciplineSnapshot[];
}> {
  const { data: sess, error: sessErr } = await supabase
    .from("einstein_sessions")
    .select("id, ended_at")
    .eq("user_id", userId)
    .eq("kind", "diagnosis")
    .not("ended_at", "is", null)
    .order("ended_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (sessErr || !sess) return { sessionId: null, snapshots: [] };

  const { data: attempts, error: aErr } = await supabase
    .from("einstein_attempts")
    .select("question_id, is_correct, einstein_questions!inner(discipline)")
    .eq("session_id", sess.id);
  if (aErr || !attempts) return { sessionId: sess.id as string, snapshots: [] };

  type Raw = {
    question_id: string;
    is_correct: boolean | null;
    einstein_questions: { discipline: string };
  };
  const byDisc: Record<string, { total: number; correct: number }> = {};
  for (const a of attempts as unknown as Raw[]) {
    const d = a.einstein_questions.discipline;
    const bucket = byDisc[d] ?? { total: 0, correct: 0 };
    bucket.total += 1;
    if (a.is_correct) bucket.correct += 1;
    byDisc[d] = bucket;
  }
  const snapshots: DisciplineSnapshot[] = Object.entries(byDisc).map(([discipline, s]) => ({
    discipline,
    total: s.total,
    correct: s.correct,
    accuracy: s.total > 0 ? s.correct / s.total : 0,
  }));
  return { sessionId: sess.id as string, snapshots };
}

/**
 * Garante que existe plano para a semana atual. Se não existir, gera + persiste.
 */
export async function ensureWeeklyPlan(params: {
  userId: string;
  weeklyHours: number;
  examDate: string | null;
}): Promise<StudyPlan> {
  const existing = await fetchCurrentWeekPlan(params.userId);
  if (existing) return existing;
  return regenerateWeeklyPlan(params);
}

/**
 * Força regeneração do plano da semana atual (sobrescreve).
 */
export async function regenerateWeeklyPlan(params: {
  userId: string;
  weeklyHours: number;
  examDate: string | null;
}): Promise<StudyPlan> {
  const { sessionId, snapshots } = await fetchLatestDiagnosisSnapshots(params.userId);
  const plan = buildStudyPlan({
    snapshots,
    weekly_hours_target: params.weeklyHours,
    exam_date: params.examDate,
    based_on_session_id: sessionId,
  });
  const { error } = await supabase.from("einstein_study_plans").upsert(
    {
      user_id: params.userId,
      week_start: plan.week_start,
      plan,
      marginal_gain_estimate: plan.marginal_gain_estimate,
      generated_at: plan.generated_at,
    },
    { onConflict: "user_id,week_start" },
  );
  if (error) throw error;
  return plan;
}
